import React, { useState } from "react";
import { api, extractError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PaddyLabAnalysisFields, DEFAULT_PADDY_LAB_VALUES } from "../forms/PaddyLabAnalysisFields";
import { PaddyLabMetricsGrid } from "./PaddyLabMetricsDisplay";
import { PADDY_LAB_METRICS } from "../../lib/paddyLabConfig";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FormField } from "../forms/FormField";
import { toast } from "sonner";
import { mapNumberFields, PADDY_LAB_NUMBER_KEYS, parseNumberInput } from "../../lib/numberInput";

const VERDICT_OPTIONS = [
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "AUTO_PASS", label: "Approve (pass)" },
  { value: "BARGAINING", label: "Bargaining / negotiate" },
  { value: "REJECTED", label: "Reject" },
];

function reportToForm(report) {
  if (!report) return { ...DEFAULT_PADDY_LAB_VALUES };
  return {
    moisture_pct: report.moisture_pct ?? "",
    yield_pct: report.yield_pct ?? "",
    broken_pct: report.broken_pct ?? "",
    damage_pct: report.damage_pct ?? "",
    green_pct: report.green_pct ?? "",
    immature_pct: report.immature_pct ?? "",
    admixture_pct: report.admixture_pct ?? "",
    pr_pct: report.pr_pct ?? "",
    length_mm: report.length_mm ?? "",
    choba_pct: report.choba_pct ?? "",
    kachri_pct: report.kachri_pct ?? "",
    custom_param_1_name: report.custom_param_1_name || "",
    custom_param_1_value: report.custom_param_1_value || "",
    custom_param_2_name: report.custom_param_2_name || "",
    custom_param_2_value: report.custom_param_2_value || "",
    notes: report.notes || "",
    bags_sampled: report.bags_sampled ?? "",
    verdict: report.verdict || "PENDING_REVIEW",
  };
}

export default function AdminPaddyLabReportEditor({
  report,
  kind = "truck",
  labCorrection = false,
  onSaved,
  onCancel,
}) {
  const [form, setForm] = useState(() => reportToForm(report));
  const [saving, setSaving] = useState(false);
  const base = kind === "composite" ? "/composite-lab-reports" : "/lab-reports";
  const flagged = Array.isArray(report?.out_of_range) ? report.out_of_range : [];
  const flaggedLabels = flagged.map(
    (key) => PADDY_LAB_METRICS.find((m) => m.key === key)?.label || key,
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = mapNumberFields({ ...form }, PADDY_LAB_NUMBER_KEYS);
      if (kind === "composite" && payload.bags_sampled !== "" && payload.bags_sampled != null) {
        payload.bags_sampled = parseNumberInput(payload.bags_sampled, { integer: true });
      } else {
        delete payload.bags_sampled;
      }
      if (labCorrection || !payload.verdict) delete payload.verdict;
      const saved = await api.patch(`${base}/${report.id}`, payload);
      if (saved?.validation_pending) {
        toast.warning("Still outside allowed range — adjust values and save again.");
      } else {
        toast.success(labCorrection ? "Corrected lab report saved" : "Lab report updated");
      }
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-muted/10 p-4 space-y-4">
      <p className="text-sm font-medium">
        {labCorrection ? "Correct lab readings" : "Review lab report (admin)"}
      </p>
      {flagged.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {labCorrection
            ? <>Parameters outside allowed lab range: <strong>{flaggedLabels.join(", ")}</strong>. Fix and save.</>
            : <>Parameters outside allowed range: <strong>{flaggedLabels.join(", ")}</strong>. Set the verdict below.</>}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
          {labCorrection
            ? "All recorded parameters are within configured limits."
            : "All recorded parameters are within configured limits. You may still set an explicit verdict."}
        </div>
      )}
      {!labCorrection && (
        <PaddyLabMetricsGrid lab={report} limits={report.lab_limits} showLimits columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <PaddyLabAnalysisFields values={form} onChange={setForm} testIdPrefix={labCorrection ? "lab-correct" : "admin-lab-edit"} />
        {kind === "composite" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bags sampled</label>
            <Input
              type="number"
              className="mt-1"
              value={form.bags_sampled}
              onChange={(e) => setForm({ ...form, bags_sampled: e.target.value })}
            />
          </div>
        )}
        {!labCorrection && (
          <FormField label="Admin verdict" required hint="Final decision on this lab report">
            <Select value={form.verdict || "PENDING_REVIEW"} onValueChange={(v) => setForm({ ...form, verdict: v })}>
              <SelectTrigger data-testid="admin-lab-verdict">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VERDICT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        )}
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={save} disabled={saving} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
          {saving ? "Saving…" : labCorrection ? "Save correction" : "Save verdict"}
        </Button>
      </div>
    </div>
  );
}
